import { Link } from 'react-router-dom';
import { useTutorStore } from '@shared/state';
import { Button } from '../ui/Button';

export function TutorLimitWarning() {
  const queryLimits = useTutorStore((s) => s.queryLimits);
  if (!queryLimits) return null;

  const { queriesUsed, queriesIncluded, canQuery, tier, limitType } = queryLimits;

  // Unlimited — no indicator needed
  if (limitType === 'unlimited') return null;

  // At limit — hard block message
  if (!canQuery) {
    return (
      <div className="px-3 py-3 border-t border-dark-border">
        <p className="text-xs text-error font-body mb-2">
          {tier === 'free'
            ? "You've used your free conversations with Professor Cogni. Upgrade to keep learning with your personal tutor."
            : `You've used all ${queriesIncluded} Professor Cogni queries this month. Upgrade to Pro for unlimited conversations.`}
        </p>
        <Link to="/pricing">
          <Button size="sm" className="w-full">
            {tier === 'free' ? 'Upgrade' : 'Upgrade to Pro'}
          </Button>
        </Link>
      </div>
    );
  }

  // Progressive indicator — always shown for limited tiers.
  const remaining = Math.max(0, queriesIncluded - queriesUsed);
  const usagePercent = queriesIncluded > 0 ? queriesUsed / queriesIncluded : 0;
  const periodLabel = limitType === 'monthly' ? ' this month' : '';

  // Colour tiers:
  //   0–60%  → muted (informational)
  //   60–80% → warning (heads-up)
  //   80%+   → error-tone (urgent, but still allows queries)
  const tone =
    usagePercent >= 0.8 ? 'text-error' :
    usagePercent >= 0.6 ? 'text-warning' :
    'text-content-muted';
  const barTone =
    usagePercent >= 0.8 ? 'bg-error' :
    usagePercent >= 0.6 ? 'bg-warning' :
    'bg-indigo-500/60';

  return (
    <div className="px-3 py-2 border-t border-dark-border">
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[11px] font-body ${tone}`}>
          {remaining} of {queriesIncluded} quer{remaining === 1 ? 'y' : 'ies'} left{periodLabel}
        </span>
        {usagePercent >= 0.6 && tier !== 'free' && (
          <Link to="/pricing" className="text-[11px] text-indigo-400 hover:text-indigo-300 font-body transition-colors">
            Upgrade
          </Link>
        )}
      </div>
      <div className="h-1 rounded-full bg-dark-subtle overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barTone}`}
          style={{ width: `${Math.min(100, usagePercent * 100)}%` }}
        />
      </div>
    </div>
  );
}
